import { useRef, useState } from "react";
import { clsx } from "clsx";
import { Download, Eye, Paperclip, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { OdtEditorScreen } from "@/features/doc_editor/components/OdtEditorScreen";
import { useEditorAvailability } from "@/features/doc_editor/hooks";
import { editorActionFor, type EditorDocKind } from "@/features/doc_editor/types";
import type { EventSide, LedFile } from "../types";

// ── klasy pól (jedno miejsce; .input-base pochodzi z index.css, klasy `.input` NIE MA) ──
export const INPUT = "input-base py-1.5 text-[13px]";
export const INPUT_SM = "input-base !py-1 text-[12px] leading-tight";
/** „Tylko ELEKTIM” / uwaga — bursztyn w obu motywach. */
export const TONE_US =
  "border-amber-400 bg-amber-50 text-amber-950 dark:border-amber-700/70 dark:bg-amber-950/40 dark:text-amber-100";
/** Coś czeka na ruch (nieprzeczytane, do potwierdzenia). */
export const TONE_WAIT = "border-amber-400 bg-amber-50/70 dark:border-amber-700/70 dark:bg-amber-950/30";
export const TONE_OK = "border-emerald-600 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/30";
export const TEXT_OK = "text-emerald-700 dark:text-emerald-400";
export const TEXT_FIRM = "text-blue-700 dark:text-blue-400";
export const TEXT_WARN = "text-amber-700 dark:text-amber-400";

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("pl-PL");
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** `yyyy-mm-dd` przesunięte o n dni — do domyślnego terminu odpowiedzi. */
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso || today()}T00:00:00`);
  if (Number.isNaN(d.getTime())) return today();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Ustawowy/przyjęty domyślny czas na odpowiedź urzędu. */
export const DEFAULT_RESPONSE_DAYS = 14;

/** Komunikat z koperty {data, errors} — `detail` albo pierwsze błędy pól. */
export function errMsg(e: unknown, fallback = "Nie udało się zapisać."): string {
  const err = e as { response?: { data?: { errors?: unknown } } };
  const errors = err?.response?.data?.errors;
  if (!errors) return fallback;
  if (typeof errors === "string") return errors;
  if (typeof errors === "object") {
    const obj = errors as Record<string, unknown>;
    if (typeof obj.detail === "string") return obj.detail;
    const parts = Object.entries(obj).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`);
    if (parts.length) return parts.join(" · ");
  }
  return fallback;
}

export function ErrorLine({ error, className, fallback }: { error: unknown; className?: string; fallback?: string }) {
  if (!error) return null;
  return (
    <p role="alert" className={clsx("mt-1.5 border-l-2 border-[var(--color-primary)] pl-2 text-xs text-[var(--color-primary)]", className)}>
      {errMsg(error, fallback)}
    </p>
  );
}

/** Kto zrobił wpis — czerwone GK / niebieska firma, jak w dzienniku. */
export function SideTag({ side, contractorName }: { side: EventSide; contractorName?: string }) {
  if (side === "system") return <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">system</span>;
  const gk = side === "gk";
  return (
    <span
      className={clsx(
        "inline-block max-w-[12rem] truncate border px-1 py-px font-mono text-[10px] uppercase leading-tight tracking-wider",
        gk
          ? "border-[var(--color-primary)] text-[var(--color-primary)]"
          : "border-blue-700 text-blue-700 dark:border-blue-400 dark:text-blue-400",
      )}
    >
      {gk ? "GK ELEKTIM" : contractorName || "firma"}
    </span>
  );
}

const CHIP = "inline-flex max-w-[16rem] items-center gap-1 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-[11px]";

/**
 * Załączniki wpisu. Z `previewKind` chip otwiera plik w przeglądarce (Collabora na pełnym
 * ekranie, TYLKO do odczytu — slot z doc_templates.editor), o ile Collabora zna ten format
 * (PDF, obrazy, dokumenty, arkusze — lista z discovery serwera); ikona pobrania zostaje
 * obok. Format spoza listy (zip…) i wpisy bez `previewKind` pobierają się jak dotąd.
 * Slot musi pasować do rodzaju wpisu — `led_title_file` autoryzuje TYLKO pliki zgłoszeń
 * tytułów, więc dziennika czy rejestru się nim nie otworzy.
 */
/** Kosz na chipie — tylko gdy wołający dał `onRemove` (samo usunięcie i potwierdzenie są u niego). */
function RemoveChip({ file, onRemove }: { file: LedFile; onRemove?: (file: LedFile) => void }) {
  if (!onRemove) return null;
  return (
    <button
      type="button"
      className="inline-flex shrink-0 text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-danger)]"
      aria-label={`Usuń dokument ${file.name}`}
      title="Usuń dokument"
      onClick={() => onRemove(file)}
    >
      <Trash2 size={11} />
    </button>
  );
}

export function FileChips({ files, className, previewKind, onRemove }: { files: LedFile[]; className?: string; previewKind?: EditorDocKind; onRemove?: (file: LedFile) => void }) {
  if (!files.length) return null;
  if (previewKind) return <PreviewFileChips files={files} className={className} kind={previewKind} onRemove={onRemove} />;
  return (
    <ul className={clsx("flex flex-wrap gap-1.5", className)}>
      {files.map((f) => (
        <li key={f.id} className={CHIP}>
          <Paperclip size={11} className="shrink-0 text-[var(--color-text-muted)]" />
          <span className="truncate"><ManagedFileLink file={{ url: f.url, original_filename: f.name }}>{f.name}</ManagedFileLink></span>
          <RemoveChip file={f} onRemove={onRemove} />
        </li>
      ))}
    </ul>
  );
}

function PreviewFileChips({ files, className, kind, onRemove }: { files: LedFile[]; className?: string; kind: EditorDocKind; onRemove?: (file: LedFile) => void }) {
  const [preview, setPreview] = useState<LedFile | null>(null);
  const { data: editorAvail } = useEditorAvailability();
  const editorExt = editorAvail?.available ? editorAvail.extensions ?? null : null;
  return (
    <ul className={clsx("flex flex-wrap gap-1.5", className)}>
      {files.map((f) => {
        const canPreview = editorActionFor(f.name, editorExt) !== null;
        return (
          <li key={f.id} className={CHIP}>
            {canPreview ? (
              <>
                <button
                  type="button"
                  onClick={() => setPreview(f)}
                  title={`Otwórz podgląd: ${f.name}`}
                  className="inline-flex min-w-0 items-center gap-1 hover:text-[var(--color-primary)] hover:underline"
                >
                  <Eye size={11} className="shrink-0 text-[var(--color-text-muted)]" />
                  <span className="truncate">{f.name}</span>
                </button>
                <ManagedFileLink
                  file={{ id: f.id, url: f.url, original_filename: f.name }}
                  icon={<Download size={11} />}
                  className="shrink-0 opacity-60 hover:opacity-100"
                  empty={null}
                >
                  <span className="sr-only">Pobierz {f.name}</span>
                </ManagedFileLink>
              </>
            ) : (
              <>
                <Paperclip size={11} className="shrink-0 text-[var(--color-text-muted)]" />
                <span className="truncate"><ManagedFileLink file={{ id: f.id, url: f.url, original_filename: f.name }}>{f.name}</ManagedFileLink></span>
              </>
            )}
            <RemoveChip file={f} onRemove={onRemove} />
          </li>
        );
      })}
      {preview && <OdtEditorScreen kind={kind} id={preview.id} onClose={() => setPreview(null)} />}
    </ul>
  );
}

export function Field({ label, children, className, hint }: { label: string; children: React.ReactNode; className?: string; hint?: string }) {
  return (
    <label className={clsx("block", className)}>
      <span className="section-label mb-1 block">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-[var(--color-text-muted)]">{hint}</span>}
    </label>
  );
}

/** Pasek „tylko ELEKTIM" — wszystko, czego firma nigdy nie widzi. */
export function OnlyUs({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={clsx("border px-3 py-2 text-xs", TONE_US, className)}>
      <span className="mr-2 bg-amber-700 px-1 py-px align-middle font-mono text-[9px] uppercase tracking-widest text-white dark:bg-amber-600">tylko ELEKTIM</span>
      {children}
    </div>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-8 text-center text-xs text-[var(--color-text-muted)]">{children}</p>;
}

/** Wybór pliku przyciskiem — strefa „przeciągnij" zostaje tylko w formularzach. */
export function FilePick({ onFiles, label = "Dodaj plik", multiple = true, accept, busy, variant = "ghost" }: {
  onFiles: (files: File[]) => void;
  label?: string;
  multiple?: boolean;
  accept?: string;
  busy?: boolean;
  variant?: "secondary" | "ghost" | "primary";
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button type="button" size="sm" variant={variant} disabled={busy} onClick={() => ref.current?.click()}>
        <Upload size={12} /> {label}
      </Button>
      <input
        ref={ref}
        type="file"
        className="hidden"
        multiple={multiple}
        accept={accept}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (files.length) onFiles(files);
        }}
      />
    </>
  );
}

/** Pole powodu + Zatwierdź / Anuluj — zamiast window.prompt. */
export function ReasonBox({ label, placeholder, confirmLabel = "Zatwierdź", onConfirm, onCancel, busy, danger, optional }: {
  label: string;
  placeholder?: string;
  confirmLabel?: string;
  onConfirm: (text: string) => void;
  onCancel: () => void;
  busy?: boolean;
  danger?: boolean;
  optional?: boolean;
}) {
  const [text, setText] = useState("");
  return (
    <div className={clsx("mt-2 border p-2.5", danger ? "border-[var(--color-primary)]" : "border-[var(--color-border)]", "bg-[var(--color-bg-elevated)]")}>
      <Field label={label}>
        <textarea className={clsx(INPUT, "min-h-[3rem]")} autoFocus value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} />
      </Field>
      <div className="mt-2 flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>Anuluj</Button>
        <Button size="sm" variant={danger ? "danger" : "primary"} disabled={busy || (!optional && !text.trim())} onClick={() => onConfirm(text.trim())}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

/** Potwierdzenie w miejscu — zamiast window.confirm. */
export function ConfirmInline({ question, onYes, onNo, busy, yesLabel = "Tak" }: { question: string; onYes: () => void; onNo: () => void; busy?: boolean; yesLabel?: string }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2 border border-[var(--color-primary)] bg-[var(--color-bg-elevated)] px-2 py-1 text-xs">
      {question}
      <Button size="sm" variant="danger" disabled={busy} onClick={onYes}>{yesLabel}</Button>
      <Button size="sm" variant="ghost" onClick={onNo}>Anuluj</Button>
    </span>
  );
}

/** Etykieta + wartość w nagłówku projektu (jak HeaderMeta w warsztacie DLC 01). */
export function HeaderMeta({ label, value, mono, children }: { label: string; value?: React.ReactNode; mono?: boolean; children?: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="section-label">{label}</dt>
      <dd className={clsx("truncate text-[13px] text-[var(--color-text-primary)]", mono && "font-mono text-xs")}>{children ?? value ?? "—"}</dd>
    </div>
  );
}
